import { AgGridChoiceFilterAdapter, AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { CheckboxWithToolTip } from 'components/WithTooltip';
import { MtrStatusValue, MtrTypeValue, SupplyByValue } from 'constantsProject';
import { useRequest } from 'features/request/useRequest';
import type { PriceListMtrRowData, PriceListMtrRowDataFilterInput } from 'gql/graphql';
import { ContractChangeStatusEnum, MtrTypeEnum } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { getDefaultFilterCleaner, usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { Checkbox, cnMixSpace } from 'uikit';
import { destroyAgGridTableFilters, formatNumberToRu, getCheckedIdsArr } from 'utils';
import { createWhereVariablesGetter } from 'utils/getWhereVariables';
import { getYesNoStatus } from 'utils/getYesNoTableStatus';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    getAgreementMtrCodeFilterItems,
    getAgreementMtrJustificationFilterItems,
    getAgreementMtrNameFilterItems,
    getAgreementMtrStatusFilterItems,
    getAgreementMtrSupplyByFilterItems,
    getAgreementMtrUnitFilterItems,
    getAgreementPriceListMtr,
} from '../AgreementsQueries';

const filters = ['code', 'name', 'unit', 'supplyBy', 'mtrType', 'status', 'justification'];

const getWhereVariables = createWhereVariablesGetter<PriceListMtrRowDataFilterInput | undefined>({
    searchStringFields: [],
    filtersToWhere: (selectedFilters: [string, boolean[] | string[]][]) =>
        selectedFilters.map(([fieldName, value]) => {
            if (
                fieldName === 'isIncludedInWorkOrder' ||
                fieldName === 'isIncludedInAct' ||
                fieldName === 'isIncludedInWorkOrderChange'
            ) {
                return {
                    [fieldName]: {
                        eq: value[0] === 'Да',
                    },
                };
            }

            return {
                [fieldName]: {
                    in: value,
                },
            };
        }),
});

export const useGetAgreementMtrItems = ({
    contractChangeId,
    refreshData,
    disabledByStatus,
    isDisabledByProjectStatus,
    needUpdateCurrentPriceList,
    contractChangeStatus,
    needUpdatePriceListHandler,
}: {
    contractChangeId: number;
    refreshData: () => void;
    disabledByStatus: boolean;
    isDisabledByProjectStatus: boolean;
    needUpdateCurrentPriceList?: boolean;
    contractChangeStatus?: ContractChangeStatusEnum;
    needUpdatePriceListHandler?: (nextState: boolean) => void;
}) => {
    const tableRef = useRef<AgGridReact>();
    const [queryWhere, setQueryWhere] = useState<any>(undefined);
    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});

    const checkedIdsArr = useMemo(() => getCheckedIdsArr(checkedIds), [checkedIds]);

    const filterChangeHandler = useCallback((value: { checkedValues: string[]; fieldName: string }) => {
        setQueryWhere((prevFilters: any) => ({ ...prevFilters, [value.fieldName]: value.checkedValues }));
    }, []);

    usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(
                    filterChangeHandler,
                    `agreementMtrItems`,
                    getDefaultFilterCleaner({
                        changeFilterHandler: filterChangeHandler,
                        setFiltersWhere: setQueryWhere,
                    }),
                ),
            }),
            [filterChangeHandler],
        ),
    );

    const variables = useMemo(
        () => ({ contractChangeId, where: getWhereVariables(queryWhere) }),
        [contractChangeId, queryWhere],
    );

    const [agreementMtrData, agreementMtrStatus, , agreementMtrRequest] = useRequest({
        requestId: 'agreementMtr',
        query: getAgreementPriceListMtr,
        variables,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'workOrderPriceList',
        requestFn: agreementMtrRequest,
        requestVariables: variables,
    });

    useEffect(() => {
        if (getRowParams?.current) {
            getRowParams?.current?.successCallback(
                agreementMtrData?.uerContractChangePriceListMtr?.items ?? [],
                agreementMtrData?.uerContractChangePriceListMtr?.totalCount,
            );
        }
    }, [
        getRowParams,
        agreementMtrData?.uerContractChangePriceListMtr?.items,
        agreementMtrData?.uerContractChangePriceListMtr?.totalCount,
    ]);

    useEffect(() => {
        if (needUpdateCurrentPriceList && needUpdatePriceListHandler) {
            tableRef?.current?.api?.refreshInfiniteCache();

            needUpdatePriceListHandler(false);
        }
    }, [needUpdateCurrentPriceList, needUpdatePriceListHandler, variables]);

    useEffect(() => {
        if (contractChangeStatus !== ContractChangeStatusEnum.Formation) {
            setCheckedIds({});
        }
    }, [contractChangeStatus]);

    const refreshAgreementMtr = useCallback(
        (skipCheckedReset?: boolean) => {
            tableRef?.current?.api?.refreshInfiniteCache();

            refreshData();

            if (!skipCheckedReset) {
                setCheckedIds({});
            }
        },
        [refreshData],
    );

    const [selection, , , setSelection] = useSelection('useGetAgreementMtrItems');
    const [pinnedItems, , , setPinned] = usePin('useGetAgreementMtrItems');

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);
    const handleChangeCheckbox = useCallback(
        (rowId: number) =>
            ({ checked }: { checked: boolean }) => {
                setCheckedIds((ids) => ({ ...ids, [rowId]: checked }));
            },
        [],
    );
    const columns = useMemo<(ColDef<PriceListMtrRowData> | ColGroupDef<PriceListMtrRowData>)[]>(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                width: 55,
                resizable: false,
                lockPosition: 'left',
                pinned: true,
                cellClass: 'center-aligned-cell',
                cellRenderer: ({ data }: { data: PriceListMtrRowData }) => {
                    if (!data) {
                        return '';
                    }

                    return (
                        data.code &&
                        (disabledByStatus ? (
                            <CheckboxWithToolTip
                                checkboxProps={{ checked: false }}
                                tooltipProps={{
                                    mode: 'mouseover',
                                    content: 'Удаление возможно только в статусе "На формировании"',
                                }}
                                fakeDisabled
                            />
                        ) : (
                            <Checkbox
                                className={cnMixSpace({ mT: '3xs' })}
                                checked={checkedIds[data.id]}
                                onChange={handleChangeCheckbox(data.id)}
                                disabled={disabledByStatus || isDisabledByProjectStatus}
                            />
                        ))
                    );
                },
            },
            {
                headerName: 'Шифр',
                field: 'code',
                width: 180,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeMtrCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getAgreementMtrCodeFilterItems,
                },
            },
            {
                field: 'name',
                headerName: 'Наименование',
                flex: 1,
                minWidth: 180,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeMtrNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getAgreementMtrNameFilterItems,
                },
            },
            {
                field: 'unit',
                headerName: 'Ед.изм.',
                width: 130,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeMtrUnitFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Ед.изм.',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getAgreementMtrUnitFilterItems,
                },
            },
            {
                field: 'unitWeight',
                headerName: 'Вес единицы, т',
                width: 120,
            },
            {
                field: 'unitCost',
                headerName: 'Стоимость единицы, руб без НДС',
                width: 136,
                valueFormatter: ({ data }) => formatNumberToRu(data?.unitCost, 2),
            },
            {
                field: 'supplyBy',
                headerName: 'Поставка',
                width: 130,
                valueFormatter: ({ data }) => (data?.supplyBy ? SupplyByValue[data?.supplyBy] : ''),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeMtrSupplyByFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Поставка',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: string }) => (value ? SupplyByValue[value] : ''),
                        },
                    ],
                    query: getAgreementMtrSupplyByFilterItems,
                },
            },
            {
                field: 'mtrType',
                headerName: 'Тип',
                valueFormatter: ({ data }) => (data?.mtrType ? MtrTypeValue[data?.mtrType] : ''),
                width: 120,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    filterItems: [
                        {
                            label: MtrTypeValue[MtrTypeEnum.Material],
                            value: MtrTypeEnum.Material,
                        },
                        {
                            label: MtrTypeValue[MtrTypeEnum.Equipment],
                            value: MtrTypeEnum.Equipment,
                        },
                    ],
                    changeFilterHandler: filterChangeHandler,
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Поставка',
                            field: 'label',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                },
            },
            {
                field: 'status',
                headerName: 'Статус записи',
                valueFormatter: ({ data }) => (data?.mtrType ? MtrStatusValue[data?.status] : ''),
                width: 130,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeMtrStatusFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Статус записи',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: string }) => (value ? MtrStatusValue[value] : ''),
                        },
                    ],
                    query: getAgreementMtrStatusFilterItems,
                },
            },
            {
                headerName: 'Используется',
                marryChildren: true,
                children: [
                    {
                        field: 'isIncludedInWorkOrder',
                        headerName: 'НЗ',
                        width: 95,
                        valueFormatter: ({ data }) => getYesNoStatus(data?.isIncludedInWorkOrder),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: filterChangeHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInAct',
                        headerName: 'Акт',
                        width: 95,
                        valueFormatter: ({ data }) => getYesNoStatus(data?.isIncludedInAct),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: filterChangeHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInWorkOrderChange',
                        headerName: 'Изм. к НЗ',
                        width: 100,
                        valueFormatter: ({ data }) => getYesNoStatus(data?.isIncludedInWorkOrderChange),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: filterChangeHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                ],
            },
            {
                field: 'justification',
                headerName: 'Обоснование',
                width: 150,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeMtrJustificationFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Обоснование',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getAgreementMtrJustificationFilterItems,
                },
            },
        ],
        [
            checkedIds,
            contractChangeId,
            disabledByStatus,
            filterChangeHandler,
            handleChangeCheckbox,
            isDisabledByProjectStatus,
        ],
    );

    const visibleColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    const handleClearChecked = useCallback(() => {
        setCheckedIds({});
    }, []);

    const columnsForTableSettings = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 5) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const handleTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: columnsForTableSettings,
                incomingColumns: visible,
                setPinned,
                setSelection,
            });
        },
        [columnsForTableSettings, setPinned, setSelection],
    );

    const totalItems = useMemo(
        () => agreementMtrData?.uerContractChangePriceListMtr?.totalCount ?? 0,
        [agreementMtrData?.uerContractChangePriceListMtr?.totalCount],
    );

    const fetchedItems = useMemo(() => {
        if (!getRowParams?.current?.endRow || !totalItems) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalItems) {
            return totalItems;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [totalItems, getRowParams?.current?.endRow]);

    const refreshFilters = useCallback(() => {
        if (tableRef) {
            destroyAgGridTableFilters({
                api: tableRef.current?.api,
                columns: filters,
            });
        }
    }, []);

    return {
        rowCount: fetchedItems,
        totalCount: totalItems,
        columns: visibleColumns,
        columnsForTableSettings,
        isColumnVisible,
        datasource: dataSource,
        requestStatus: agreementMtrStatus,
        handleTableSettingsConfirm,
        filterChangeHandler,
        checkedIdsArr,
        handleClearChecked,
        refreshAgreementMtr,
        refreshFilters,
        totalItems,
        fetchedItems,
        tableRef,
    };
};
